import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Progress } from '../../../globalTypes'
import { useOnClickOutside } from '../../../hooks'
import { useInView } from '../../../hooks/useInView'
import { Checkbox } from '../../controls/Checkbox/Checkbox'
import { Label } from '../../form/Label/Label'
import * as Styled from './styles'

export type CheckboxTreeOption = {
  id: string
  title: string
  hasChildren: boolean
  parent?: string
  [key: string]: unknown
}

type Selected = Array<string>

type CustomOptionTitle = (item: CheckboxTreeOption) => React.ReactElement

export type CheckboxTreeSelectProps = {
  options?: Array<CheckboxTreeOption>
  selected?: Selected
  onChange: (selected: Selected) => void
  customOptionTitle?: CustomOptionTitle
  label?: string
  placeholder?: string
  isDisabled?: boolean
  filter?: (item: CheckboxTreeOption) => boolean
  noOptionText?: string
  loadingText?: string
  selectedMultipleItemsNames?: string
  onMenuScrollToBottom?: () => void
  onSearchChange?: (search: string) => void
  progress?: Progress
  disableFilterOnSearchChange?: boolean
}

type ItemsMap = {
  [id: string]: MapItem
}

type NestedMap = {
  [id: string]: number
}

type MapItem = CheckboxTreeOption & {
  children: Array<string>
  total: number
  isChecked: boolean
  childrenMapped: Array<MapItem>
  checkedChildren: number
  isChildrenEqualToTotal: boolean
}

type OptionsProps = {
  item: MapItem
  onChange: (id: string, only?: boolean) => void
  customOptionTitle?: CustomOptionTitle
  innerRef?: (instance: HTMLDivElement | null) => void
}

function useOptionsMapping(
  options: Array<CheckboxTreeOption>,
  filter?: (item: CheckboxTreeOption) => boolean,
  disableFilterOnSearchChange?: boolean,
): [ItemsMap, NestedMap, Array<string>] {
  return useMemo(() => {
    const map: ItemsMap = {}
    const nested: NestedMap = {}
    const list: Array<string> = []
    const filtered = !disableFilterOnSearchChange && filter ? options.filter(filter) : options

    for (const item of filtered) {
      map[item.id] = {
        ...item,
        children: [],
        total: 0,
        isChecked: false,
        checkedChildren: 0,
        childrenMapped: [],
        isChildrenEqualToTotal: false,
      }
    }

    for (const item of filtered) {
      if (item.hasChildren) {
        nested[item.id] = 0
      }

      if (item.parent && map[item.parent]) {
        const parentId = item.parent
        map[parentId].children.push(item.id)
        map[parentId].total++
      } else {
        list.push(item.id)
      }
    }
    return [map as ItemsMap, nested, list]
  }, [disableFilterOnSearchChange, filter, options])
}

function useFilteredList(
  list: Array<string>,
  search: string,
  map: ItemsMap,
  checked: string[],
  childrenCheckedMap: NestedMap,
  disableFilterOnSearchChange?: boolean,
): Array<MapItem | null> {
  const isItemFitsSearch = useCallback(id => map[id].title.toLowerCase().includes(search.toLowerCase()), [search, map])
  return useMemo(() => {
    return list
      .map(id => {
        const isChecked = checked.includes(id)

        if (!search && !map[id].hasChildren) {
          return { ...map[id], isChecked }
        }

        // if item is not a parent or a child
        if (!map[id].parent && !map[id].total) {
          return isItemFitsSearch(id) && !disableFilterOnSearchChange ? { ...map[id], isChecked } : null
        }

        // if item is a parent
        if ((!search && !map[id].hasChildren) || (isItemFitsSearch(id) && !disableFilterOnSearchChange)) {
          return {
            ...map[id],
            isChecked,
            childrenMapped: map[id].children.map(childId => ({
              ...map[childId],
              isChecked: checked.includes(childId),
            })),
            checkedChildren: childrenCheckedMap[id],
          }
        }

        // if a parent doesn't fit check it's children
        const filteredChildren = !disableFilterOnSearchChange
          ? map[id].children.filter(isItemFitsSearch)
          : map[id].children
        if (filteredChildren.length) {
          return {
            ...map[id],
            isChecked,
            childrenMapped: filteredChildren.map(childId => ({
              ...map[childId],
              isChecked: checked.includes(childId),
            })),
            isChildrenEqualToTotal: filteredChildren.length === map[id].total,
            childrenChecked: childrenCheckedMap[id],
          }
        }

        return null
      })
      .filter(Boolean)
  }, [checked, childrenCheckedMap, disableFilterOnSearchChange, isItemFitsSearch, list, map, search])
}

const SimpleOption: React.FC<OptionsProps> = ({ item, onChange, customOptionTitle, innerRef }) => {
  return (
    <Styled.Option isChild={!!item.parent} onClick={() => onChange(item.id)} ref={innerRef}>
      {/* eslint-disable-next-line @typescript-eslint/no-empty-function */}
      <Checkbox isChecked={item.isChecked} onChange={() => {}} />
      <Styled.Label>{customOptionTitle ? customOptionTitle(item) : item.title}</Styled.Label>
    </Styled.Option>
  )
}

const ParentOption: React.FC<OptionsProps> = ({ item, onChange, customOptionTitle }) => {
  const { t } = useTranslation('ui')
  const [isExpanded, setIsExpanded] = useState(false)
  const onlyChildrenChecked = item.checkedChildren === item.total && !item.isChecked
  const someChildrenChecked = item.checkedChildren > 0 && item.checkedChildren !== item.total
  const onlyParentChecked = !item.checkedChildren && item.isChecked

  const isSemiChecked = onlyChildrenChecked || someChildrenChecked || onlyParentChecked
  const childrenFiltered = (!!item.total && item.childrenMapped.length < item.total) || item.isChildrenEqualToTotal

  const onToggle = e => {
    e.stopPropagation()
    setIsExpanded(!isExpanded)
  }

  const handleOptionSelect = useCallback(() => onChange(item.id), [item.id, onChange])
  const handleOnlyBtnClick = useCallback(() => onChange(item.id, true), [item.id, onChange])

  return (
    <>
      <Styled.Option
        title={childrenFiltered ? t('CheckboxTreeSelect.nestedOptionsFilteredWarning') : ''}
        data-id={item.id}
        isParent
      >
        <Styled.NestedIndicator onClick={onToggle} data-is-open={isExpanded || childrenFiltered} />
        <Styled.OptionInner>
          <Checkbox
            isChecked={item.isChecked || isSemiChecked}
            isPartlyChecked={isSemiChecked}
            partialCheckType={item.isChecked ? 'square' : 'circle'}
            onChange={handleOptionSelect}
          />
          <label onClick={handleOptionSelect}>{customOptionTitle ? customOptionTitle(item) : item.title}</label>
          {!childrenFiltered && (
            <Styled.Only onClick={handleOnlyBtnClick}>
              {item.isChecked ? t('CheckboxTreeSelect.deselectOnly') : t('CheckboxTreeSelect.selectOnly')}
            </Styled.Only>
          )}
        </Styled.OptionInner>
      </Styled.Option>
      <Styled.NestedList isOpen={isExpanded || childrenFiltered}>
        {item.childrenMapped.map((child: MapItem) => (
          <SimpleOption item={child} key={child.id} onChange={onChange} customOptionTitle={customOptionTitle} />
        ))}
      </Styled.NestedList>
    </>
  )
}

export const CheckboxTreeSelect: React.FC<CheckboxTreeSelectProps> = ({
  options = [],
  onChange,
  customOptionTitle,
  selected = [],
  placeholder,
  selectedMultipleItemsNames,
  filter,
  isDisabled,
  label,
  loadingText,
  noOptionText,
  onMenuScrollToBottom,
  onSearchChange,
  progress,
  disableFilterOnSearchChange,
}) => {
  const { t } = useTranslation('ui')
  const [map, nested, list] = useOptionsMapping(options, filter, disableFilterOnSearchChange)
  const [checked, setChecked] = useState<Array<string>>(selected)
  const [checkedChildrenQty, setCheckedChildrenQty] = useState(nested)
  const [search, setSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const { ref: lastItemRef } = useInView(onMenuScrollToBottom, 0.25)

  const handleClickOutside = useCallback(() => {
    setIsOpen(false)
    setSearch('')
    onSearchChange?.('')
  }, [onSearchChange])

  const updateCheckedChildrenQty = (parentId: string, qty: number) => {
    setCheckedChildrenQty({
      ...checkedChildrenQty,
      [parentId]: Math.max(0, checkedChildrenQty[parentId] + qty),
    })
  }

  const handleCheckedChange = (id: string, only?: boolean) => {
    const set = new Set<string>(checked)

    const toggle = () => (set.has(id) ? set.delete(id) : set.add(id))

    const manageBranch = (id: string, mode: 'add' | 'remove') => {
      mode === 'add' ? set.add(id) : set.delete(id)
      map[id].children.forEach(childId => (mode === 'add' ? set.add(childId) : set.delete(childId)))
      setCheckedChildrenQty({
        ...checkedChildrenQty,
        [id]: mode === 'add' ? map[id].total : 0,
      })
    }

    // simple item (no parent or children) or only parent item
    if ((!map[id].parent && !map[id].total) || (map[id].total && only)) {
      toggle()
    }

    // parent item and all it's children (whole branch)
    if (map[id].total && !only) {
      manageBranch(id, checkedChildrenQty[id] !== map[id].total ? 'add' : 'remove')
    }

    // child item
    const parent = map[id].parent
    if (parent) {
      updateCheckedChildrenQty(parent, set.has(id) ? -1 : 1)
      toggle()
    }

    setChecked([...set])
    onChange([...set])
    inputRef.current && inputRef.current.focus()
  }

  const handleSearchChange = e => {
    setSearch(e.target.value)

    onSearchChange?.(e.target.value)
    if (!isOpen) {
      setIsOpen(true)
    }
  }

  const handleReset = e => {
    e.preventDefault()
    e.stopPropagation()
    setChecked([])
    setCheckedChildrenQty(nested)
    setSearch('')
    onSearchChange?.('')
    onChange([])
  }

  const handleControlClick = () => {
    if (!isDisabled) {
      setIsOpen(!isOpen)
      inputRef.current && !isOpen && inputRef.current.focus()
    }
  }

  useOnClickOutside(wrapperRef, handleClickOutside)

  const filteredList = useFilteredList(list, search, map, checked, checkedChildrenQty, disableFilterOnSearchChange)

  let value = placeholder

  if (checked.length > 1) {
    value = t('CheckboxTreeSelect.multipleText', { selectedMultipleItemsNames })
  } else if (checked.length === 1 && map[checked[0]]) {
    value = map[checked[0]].title
  }

  const isLoading = progress === Progress.WORK || progress === Progress.IDLE

  return (
    <Styled.Wrapper ref={wrapperRef}>
      {!!label && (
        <Styled.LabelWrapperStyled>
          <Label>{label}</Label>
        </Styled.LabelWrapperStyled>
      )}
      <Styled.Control
        onClick={handleControlClick}
        data-is-open={isOpen}
        data-is-empty={!checked.length}
        data-is-disabled={isDisabled}
      >
        <Styled.ValueContainer data-is-typing={!!search}>
          <Styled.Value>{value}</Styled.Value>
          <Styled.SearchField
            type="text"
            value={search}
            onChange={handleSearchChange}
            ref={inputRef}
            onFocus={() => setIsOpen(true)}
          />
        </Styled.ValueContainer>
        <Styled.ClearButton onClick={handleReset} />
        <Styled.DropdownIndicator data-is-open={isOpen} />
      </Styled.Control>
      {isOpen && (
        <Styled.List>
          {filteredList.length ? (
            filteredList.map((item, index) => {
              if (!item) {
                return null
              }

              if (item.total && item.childrenMapped.length) {
                return (
                  <ParentOption
                    key={item.id}
                    item={item}
                    onChange={handleCheckedChange}
                    customOptionTitle={customOptionTitle}
                  />
                )
              }

              return (
                <SimpleOption
                  key={item.id}
                  item={item}
                  onChange={handleCheckedChange}
                  customOptionTitle={customOptionTitle}
                  innerRef={index === filteredList.length - 1 ? lastItemRef : undefined}
                />
              )
            })
          ) : (
            <Styled.EmptyMsg>
              {!!options?.length && !isLoading && (noOptionText || t('CheckboxTreeSelect.noOptionsText'))}
              {isLoading && (loadingText || t('CheckboxTreeSelect.loadingText'))}
            </Styled.EmptyMsg>
          )}
        </Styled.List>
      )}
    </Styled.Wrapper>
  )
}
